import { v4 } from 'uuid';

import { blankComponenetDimensionOptionsCfn } from './functions';
import { ComponentOptions } from '../types';
import { RecordData } from '../../helpers-classes';
import { Rules } from '../../blueprint-classes';
import { Size } from '../../types';
import { ComponentDataIn, ComponentDimensionOptions, ComponentDynamicSvg, DefaultPlacement, ImageCorrection, SkuOverheadMode } from './types';
import { ComponentItemData } from '../data/component-item-data.class';

// Landing-zone default when the catalog doesn't send the field yet (pre-deploy APIs):
// mirrors scripts/seed-landing-zone-defaults.ts — appliance categories on, the
// non-appliance families and top-only drop-ins off. A served value always wins.
const LANDING_ZONE_EXCLUDED_CATEGORY_IDS = new Set([
	'c5951b77-4199-477b-b75a-af8d2fcdca0f', // insulated-jacket
	'796bddfe-2031-434d-a9ae-7d24874a0c55', // other
	'914f22bb-f874-4cb0-bebd-4a6d7e170ae5', // cutout
	'0ce0d179-9b11-4cfa-8305-296a9a82508a', // outlet
	'264fce5b-56c0-4cb1-a672-c34be8b29838', // hole
	'285cb107-ea18-49f4-9cf6-945305b6193c', // system
	'f7219742-5b07-4032-bcd7-0051950e9ce7', // vent
	'f7219742-5b07-4032-bcd7-0051950eadf5', // passthrough
	'a1b2c3d4-e5f6-7890-abcd-ef1234567890'  // spacer
]);

const landingZoneDefault = (categoryId: string | null, placement: string | null): boolean =>
	categoryId !== null && !LANDING_ZONE_EXCLUDED_CATEGORY_IDS.has(categoryId) && placement !== 'top only';

export const componentData = (options: ComponentOptions<ComponentDataIn>): ComponentDataIn => {
	// const { noteMessage = '', artifactId = null } = params || {};
	const rd = new RecordData<ComponentDataIn>(options.dataIn);
	// Look up hsProductId from asset data if not in saved data (for override items)
	const sku = options.dataIn?.sku;
	const assetData = sku ? ComponentItemData.getDataBySku(sku) : null;

	const record: ComponentDataIn = {
		id: rd.useNewOrPartial<string>('id', v4()),
		vendorId: rd.useNewOrDefault<string>('vendorId', null),
		sku: rd.useNewOrDefault<string>('sku', null),
		label: rd.useNewOrDefault<string>('label', null),
		displayName: rd.useNewOrDefault<string>('displayName', null),
		customLabel: rd.useNewOrDefault<string>('customLabel', null),
		description: rd.useNewOrDefault<string>('description', null),
		blueprintDescription: rd.useNewOrDefault<string>('blueprintDescription', null),
		categoryId: rd.useNewOrDefault<string>('categoryId', null),
		placement: rd.useNewOrDefault<string>('placement', null),
		application: rd.useNewOrDefault<string>('application', null),
		applianceModule: rd.useNewOrDefault<string>('applianceModule', null),
		cutout: rd.useNewOrDefault<Size>('cutout', null),
		size: rd.useNewOrDefault<Size>('size', null),
		distanceAboveCountertop: rd.useNewOrDefault<number>('distanceAboveCountertop', null),
		distanceFrontOfFace: rd.useNewOrDefault<number | null>('distanceFrontOfFace', null),
		distanceBackOfFace: rd.useNewOrDefault<number | null>('distanceBackOfFace', null),
		distanceBelowCountertop: rd.useNewOrDefault<number>('distanceBelowCountertop', null),
		defaultPlacement: rd.useNewOrDefault<DefaultPlacement | null>('defaultPlacement', null),
		imageCorrection: rd.useNewOrDefault<ImageCorrection>('imageCorrection', null),
		dimensions: { ...blankComponenetDimensionOptionsCfn(), ...rd.useNewOrDefault<ComponentDimensionOptions>('dimensions', null) },
		showLabel: rd.useNewOrDefault<boolean>('showLabel', null),
		showImage: rd.useNewOrDefault<boolean>('showImage', null),
		showInOverhead: rd.useNewOrDefault<boolean>('showInOverhead', true),
		showInSkuOverhead: rd.useNewOrDefault<boolean>('showInSkuOverhead', true),
		// #392/#458: this normalizer never carried showFuelSku through from raw catalog data, so
		// Component's constructor (`data.showFuelSku ?? true`) always saw undefined and defaulted
		// to true -- no catalog/DB value for this field could ever reach a live Component, which is
		// why toggling it in Product Data (or the DB directly) never removed the LP/NG suffix or the
		// "Gas Hose Not Included" note from a pellet grill.
		showFuelSku: rd.useNewOrDefault<boolean>('showFuelSku', true),
		// Catalog flag: this appliance needs a landing zone on scratch-coat kitchens (#340).
		landingZone: rd.useNewOrDefault<boolean>('landingZone', landingZoneDefault(rd.useNewOrDefault<string>('categoryId', null), rd.useNewOrDefault<string>('placement', null))),
		skuOverheadMode: rd.useNewOrDefault<SkuOverheadMode>('skuOverheadMode', 'sku'),
		skuColorPreset: rd.useNewOrDefault<string>('skuColorPreset', 'White'),
		rules: rd.useNewOrDefault<Rules>('rules', { action: [], validation: [] }),
		active: rd.useNewOrDefault<boolean>('active', true),
		fuel: rd.useNewOrDefault<boolean>('fuel', false),
		fuelFamily: rd.useNewOrDefault<string | null>('fuelFamily', assetData?.fuelFamily ?? null),
		fuelType: rd.useNewOrDefault<string | null>('fuelType', assetData?.fuelType ?? null),
		render: rd.useNewOrDefault<boolean>('render', true),
		svg: rd.useNewOrDefault<ComponentDynamicSvg>('svg', null),
		msrp: rd.useNewOrDefault<number>('msrp', 0),
		hsProductId: rd.useNewOrDefault<string | null>('hsProductId', assetData?.hsProductId ?? null),
		modelFile: rd.useNewOrDefault<string | null>('modelFile', assetData?.modelFile ?? null),
		imageFile: rd.useNewOrDefault<string | null>('imageFile', assetData?.imageFile ?? null),
		modelOffset: assetData?.modelOffset ?? rd.useNewOrDefault<{ x: number; y: number; z: number }>('modelOffset', { x: 0, y: 0, z: 0 }),
		modelRotation: assetData?.modelRotation ?? rd.useNewOrDefault<{ x: number; y: number; z: number }>('modelRotation', { x: 0, y: 0, z: 0 }),
		sleeveCompatible: rd.useNewOrDefault<boolean>('sleeveCompatible', false),
		sleeveDepth: rd.useNewOrDefault<number>('sleeveDepth', null),
		ruleManaged: rd.useNewOrDefault<boolean>('ruleManaged', true),
		fullHeight: rd.useNewOrDefault<boolean>('fullHeight', false),
		aiContext: rd.useNewOrDefault<string | null>('aiContext', null),
		placeholderModelSku: rd.useNewOrDefault<string | null>('placeholderModelSku', null)
	};

	return record;
};
